import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import styles from "./KnowledgeBaseView.scss";
import Chevron from "components/icons/Chevron";
import { KnowledgeBaseArticle } from "services/news/KnowledgeBaseService";
import { useTheme } from "utils/useTheme";

import spinnerSvg from "assets/images/icons/spinner.svg";

interface Props {
    page: KnowledgeBaseArticle;
    depth?: number;
    expandedPages: Set<string>;
    childrenCache: { [key: string]: KnowledgeBaseArticle[] };
    selectedPage?: KnowledgeBaseArticle;
    onCategoryToggle: (pageId: string) => void;
    onArticleClick: (page: KnowledgeBaseArticle) => void;
}

export const KnowledgeBasePageTree: React.FC<Props> = React.memo(
    ({ page, depth = 0, expandedPages, childrenCache, selectedPage, onCategoryToggle, onArticleClick }) => {
        const { t } = useTranslation();
        const theme = useTheme();
        const isExpanded = expandedPages.has(page.id);
        const children = childrenCache[page.id];
        const hasChildren = children !== undefined && children.length > 0;
        const isChildrenFetched = Object.prototype.hasOwnProperty.call(childrenCache, page.id);
        const isSelected = selectedPage?.id === page.id;

        const renderPageItem = () => (
            <div className={styles.pageItem}>
                {hasChildren ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onCategoryToggle(page.id);
                        }}
                        className={styles.pageButton}
                    >
                        <Chevron
                            color={theme.iconFillColor}
                            width={20}
                            height={20}
                            className={classNames(styles.chevronIcon, {
                                [styles.expanded]: isExpanded,
                                [styles.collapsed]: !isExpanded,
                            })}
                        />
                    </button>
                ) : isChildrenFetched ? (
                    <span className={styles.pageSpacer} />
                ) : (
                    <div className={styles.loadingIcon}>
                        <img src={spinnerSvg} alt={t("Common.loading")} />
                    </div>
                )}
                <div
                    onClick={() => {
                        onArticleClick(page);
                        if (hasChildren && !isExpanded) {
                            onCategoryToggle(page.id);
                        }
                    }}
                    className={classNames(styles.pageTitle, styles.clickable, {
                        [styles.selected]: isSelected,
                    })}
                >
                    {isSelected && !hasChildren && <span className={styles.selected} />}
                    <span className={styles.pageTitleText}>{page.title}</span>
                </div>
            </div>
        );

        return (
            <div key={page.id} className={styles.pageTreeItem} data-page-id={page.id}>
                {depth === 0 ? (
                    <div className={styles.navigationBox}>
                        {renderPageItem()}
                        {isExpanded && children && children.length > 0 && (
                            <div className={styles.pageChildren}>
                                {children.map((child) => (
                                    <KnowledgeBasePageTree
                                        key={child.id}
                                        page={child}
                                        depth={depth + 1}
                                        expandedPages={expandedPages}
                                        childrenCache={childrenCache}
                                        selectedPage={selectedPage}
                                        onCategoryToggle={onCategoryToggle}
                                        onArticleClick={onArticleClick}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        {renderPageItem()}
                        {isExpanded && children && children.length > 0 && (
                            <div className={styles.pageChildren}>
                                {children.map((child) => (
                                    <KnowledgeBasePageTree
                                        key={child.id}
                                        page={child}
                                        depth={depth + 1}
                                        expandedPages={expandedPages}
                                        childrenCache={childrenCache}
                                        selectedPage={selectedPage}
                                        onCategoryToggle={onCategoryToggle}
                                        onArticleClick={onArticleClick}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
);
